import bytes from 'bytes';
import React, {ReactElement} from 'react';

import {ProgressCell, TEditableColumn, TFieldStateType} from 'components/editable-table';
import {IFieldMeta} from 'entities/metadata';
import {TEntity, TEntityValue} from 'modules/data';
import {ReactComponent as DeleteIcon} from 'shared/assets/close.svg';
import {createOptionItemsTransformer} from 'shared/utils';
import {
    IDocumentTypeResponse,
    IDocumentUploadFile,
    IEntityFileUploadState,
    EFileSizeFormat,
    EFileUploadStatus,
    IFileUploadType,
    EXsdValidationStatusCodes,
} from 'store/slices/documents-uploading-slice';
import {IRegionDto} from 'store/slices/regions-slice';


import './document.less';

type TOptionsType = {
    handleDelete: (tableRow: TEntity) => void;
    handleEdit: (tableRow: TEntity, args: IEditFormArgs) => void;
    fileSizeFormat: string | null;
    progress?: {
        fileUploadSlice: IEntityFileUploadState;
        fieldKey: string;
    };
};

const createFieldState = (
    uniqueFieldKey: string = '', activityList?: Record<string, IFileUploadType>,
) => (fieldName: string, record: Record<string, any>): TFieldStateType => {
    const uniqueFieldValue = record?.[uniqueFieldKey];

    return activityList?.[uniqueFieldValue]?.editFieldsPermission?.[fieldName] || {} as TFieldStateType;
};

const renderXmlValidateStatus = (
    uniqueFieldKey: string = '', activityList?: Record<string, IFileUploadType>,
) => (_: any, record: Record<string, any>) => {
    const uniqueFieldValue = record?.[uniqueFieldKey];

    return activityList?.[uniqueFieldValue]?.validatedMeaning;
};

const renderFileSize = (size: number, mask: string | null): string => {
    if (mask == null) {
        return String(size);
    }
    let flexMask: string | null = null;
    if (mask.toUpperCase() === EFileSizeFormat.FLEX) {
        if (size < 1024) flexMask = 'B';
        if (size >= 1024 && size < 1024 * 1024) flexMask = 'Kb';
        if (size >= 1024 * 1024 && size < 1024 * 1024 * 1024) flexMask = 'Mb';
        if (size >= 1024 * 1024 * 1024) flexMask = 'Gb';
    }
    return bytes.format(size, {
        unit: (flexMask || mask) as bytes.Unit,
        unitSeparator: ' ',
    });
};

const renderXsdValidationStatus = () => (value: string, row: IDocumentUploadFile): ReactElement => {
    switch (row.xsdValidationStatusCode) {
    case EXsdValidationStatusCodes.NOT_APPLICABLE:
        return <span>{value}</span>;
    case EXsdValidationStatusCodes.SUCCESS:
        return <span className="success-text">{value}</span>;
    default:
        return <span className="critic-text">{value}</span>;
    }
};

export interface IEditFormArgs {
    type?: string | number;
    branchId?: string;
}

export const CONFIRM_MODAL_VIEWS = {
    XSD_VALIDATION: 'XSD_VALIDATION',
    DUPLICATE_VALIDATION: 'DUPLICATE_VALIDATION',
} as const;

export type TConfirmModalViews = keyof typeof CONFIRM_MODAL_VIEWS;

export enum EKeyCols {
    progress = 'progress',
    parsing = 'parsing',
    title = 'title',
    type = 'type',
    sectionCode = 'sectionCode',
    subsectionCode = 'subsectionCode',
    fileName = 'fileName',
    fileSize = 'fileSize',
    xmlValidateStatusCode = 'xmlValidateStatusCode',
    uploadDate = 'uploadDate',
    correctionNumber = 'correctionNumber',
    regNum = 'regNum',
    actions = 'actions',
    docBranchName = 'docBranchName',
    templateCode = 'templateCode',
    xsdValidationStatus = 'xsdValidationStatus',
}

export const generateFileColumnsByMetaData = (
    cols: IFieldMeta,
    regions: IRegionDto[],
    documentTypesOnUpload: IDocumentTypeResponse[],
    branchNames: IDocumentTypeResponse[],
    {
        handleDelete,
        fileSizeFormat,
        progress,
    }: TOptionsType,
): TEditableColumn => {
    let columnSpecificProps: Record<string, any> = {};
    // eslint-disable-next-line default-case
    switch (cols.key) {
    case EKeyCols.progress:
        columnSpecificProps = {
            render: (_: any, record: { [x: string]: any }) => {
                const {fieldKey, fileUploadSlice} = progress || {};
                const uniqueValue = record[fieldKey as string];
                const percent = fileUploadSlice?.activityList?.[uniqueValue]?.uploadPercent ?? 0;
                const status = fileUploadSlice?.activityList?.
                    [uniqueValue]?.uploadStatus as EFileUploadStatus | undefined;

                return (
                    <ProgressCell
                        status={status}
                        percent={percent}
                    />
                );
            },
        };
        break;
    case EKeyCols.parsing:
        columnSpecificProps = {
            render: (_: any, record: Record<string, any>) => {
                const {fieldKey, fileUploadSlice} = progress || {};
                const uniqueValue = record[fieldKey as string];
                const percent = fileUploadSlice?.activityList?.[uniqueValue]?.parsingPercent ?? 0;
                const status = fileUploadSlice?.activityList?.
                    [uniqueValue]?.parsingStatus as any as EFileUploadStatus | undefined;

                return (
                    <ProgressCell
                        status={status}
                        percent={percent}
                    />
                );
            },
        };
        break;
    case EKeyCols.type: {
        columnSpecificProps = {
            generateInputProps: (_: string, record?: TEntity) => ({
                defaultValue: record?.type
                    ? (documentTypesOnUpload.find(
                        el => el.id === record?.type,
                    ))?.id : undefined,
                options: createOptionItemsTransformer('meaning', 'id')(documentTypesOnUpload),
            }),
        };
        break;
    }
    case EKeyCols.sectionCode:
        columnSpecificProps = {
            render: (text: string) => (regions.find(region => region.regionCode === text)?.regionFullName),
            generateInputProps: () => ({
                options: createOptionItemsTransformer('regionFullName', 'regionCode')(regions),
            }),
        };
        break;
    case EKeyCols.subsectionCode:
        columnSpecificProps = {
            generateInputProps: (_: string, record?: TEntity) => {
                const subregionList = regions
                    .find(({regionCode}: IRegionDto) => regionCode === record?.sectionCode)?.subregionList || [];
                return {
                    options: createOptionItemsTransformer('name', 'code')(subregionList),
                };
            },
        };
        break;
    case EKeyCols.fileSize:
        columnSpecificProps = {
            render: (size: string | number) => renderFileSize(+size, fileSizeFormat),
        };
        break;
    case EKeyCols.xmlValidateStatusCode:
        columnSpecificProps = {
            render: renderXmlValidateStatus(progress?.fieldKey, progress?.fileUploadSlice?.activityList),
        };
        break;
    case EKeyCols.uploadDate:
    case EKeyCols.correctionNumber:
    case EKeyCols.regNum:
        columnSpecificProps = {
            getFieldState: createFieldState(progress?.fieldKey, progress?.fileUploadSlice?.activityList),
        };
        break;
    case EKeyCols.actions:
        columnSpecificProps = {
            render: (_: any, record: Record<string, TEntityValue>) => (
                <DeleteIcon
                    style={{cursor: 'pointer', marginTop: 5}}
                    onClick={() => handleDelete(record)}
                />
            ),
        };
        break;
    case EKeyCols.templateCode:
        columnSpecificProps = {
            generateInputProps: (_: string, record: IDocumentUploadFile) => {
                if (record.templatesTypeOptions) {
                    return {
                        entries: createOptionItemsTransformer('meaning', 'lookupCode')(record.templatesTypeOptions),
                        value: record.templatesTypeOptions.length === 1 && record.templatesTypeOptions[0].meaning,
                        required: record.templatesTypeOptions.length > 1,
                        settings: {
                            placeholder: 'Не выбрано',
                            isDisabled: record?.templatesTypeOptions.length <= 0,
                        },
                    };
                }
                return null;
            },
        };
        break;
    case EKeyCols.docBranchName: {
        columnSpecificProps = {
            generateInputProps: (_: string, record?: TEntity) => {
                const docTypeBranchId = documentTypesOnUpload.find(el => el.id === record?.branchId)?.id;
                const defaultBranchId = branchNames.find(branch => branch?.attribute1 === 'MAIN')?.id;
                return {
                    defaultValue: record?.branchId
                        ? docTypeBranchId
                        : defaultBranchId,
                    options: createOptionItemsTransformer('meaning', 'id')(branchNames),
                };
            },
        };
        break;
    }
    case EKeyCols.xsdValidationStatus:
        columnSpecificProps = {
            width: '220px',
            render: renderXsdValidationStatus(),
        };
        break;
    }
    return {
        ...cols,
        dataIndex: cols.dataIndex || cols.key,
        getFieldState: undefined,
        inputProps: undefined,
        ...columnSpecificProps,
    };
};

export const hasHeaderOrganization = (
    hasBranchesLinkingFlag?: boolean,
    branchNames?: IDocumentTypeResponse[],
) => {
    if (!hasBranchesLinkingFlag) return true; // раздел филиалов не используется, проверка не нужна
    return branchNames?.some(branch => branch?.attribute1 === 'MAIN');
};
